<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  points: { type: [Number, String], default: 0 },
  progress: { type: [Number, String], default: 0 },
  target: { type: [Number, String], default: 0 },
  completed: { type: Boolean, default: false },
  icon: { type: String, default: 'i-lucide-target' },
});

const emit = defineEmits(['action']);
const { t } = useI18n();
const progressPercent = computed(() => {
  if (props.completed) return 100;
  if (Number(props.target) <= 0) return 0;
  return Math.min(100, (Number(props.progress) / Number(props.target)) * 100);
});
</script>

<template>
  <article
    class="rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card"
  >
    <div class="flex items-start gap-3">
      <span
        class="grid size-10 shrink-0 place-items-center rounded-full bg-lais-primary-50 text-lais-primary"
        aria-hidden="true"
      >
        <span class="size-5" :class="icon" />
      </span>
      <div class="min-w-0 flex-1">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h3 class="truncate text-sm font-semibold text-lais-text">
              {{ title }}
            </h3>
            <p
              v-if="description"
              class="mt-1 text-xs leading-relaxed text-lais-text-body"
            >
              {{ description }}
            </p>
          </div>
          <span class="shrink-0 text-sm font-bold text-lais-primary">
            {{
              t('GAMIFICATION.BAR.MISSION_POINTS', {
                points,
                unit: t('GAMIFICATION.BAR.POINTS_SHORT'),
              })
            }}
          </span>
        </div>
        <div class="mt-4 flex items-center gap-3">
          <progress
            class="h-1.5 flex-1 overflow-hidden rounded-full [appearance:none] [&::-moz-progress-bar]:bg-lais-primary [&::-webkit-progress-bar]:bg-lais-primary-50 [&::-webkit-progress-value]:bg-lais-primary"
            :value="progressPercent"
            max="100"
          />
          <span
            v-if="target"
            class="text-[11px] font-medium text-lais-text-muted"
          >
            {{ t('GAMIFICATION.BAR.PROGRESS', { progress, target }) }}
          </span>
          <span
            v-else-if="completed"
            class="text-[11px] font-semibold text-lais-primary"
          >
            {{ t('GAMIFICATION.COMMON.COMPLETED') }}
          </span>
        </div>
      </div>
    </div>
    <button
      v-if="!completed"
      type="button"
      class="mt-4 inline-flex min-h-8 items-center gap-1.5 rounded-lg border border-lais-primary px-3 text-xs font-semibold text-lais-primary transition hover:bg-lais-primary-50 focus-visible:outline-none focus-visible:shadow-lais-focus"
      @click="emit('action')"
    >
      {{ t('GAMIFICATION.COMMON.VIEW_MISSION') }}
      <span class="i-lucide-arrow-up-right size-3.5" aria-hidden="true" />
    </button>
  </article>
</template>
